import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4PersonalPropertyCoverageArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'How Much Personal Property Coverage Do I Need for Renters Insurance? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn how much personal property coverage you need on a renters insurance policy, with room-by-room estimates, common limits, and tips for Texas renters.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/how-much-personal-property-coverage-do-i-need-for-renters-insurance"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>How much personal property coverage do I need for renters insurance?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              A practical guide to choosing a personal property limit on your HO-4 policy — what to count, typical ranges,
              and when to go higher. New to HO-4? Start with{' '}
              <Link to="/knowledge-base/what-is-ho4-renters-insurance" style={articleLinkStyle}>what renters insurance covers</Link>.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Most renters need enough personal property coverage to replace everything they own if it were destroyed
                tomorrow. A common starting point is <strong>$20,000 to $40,000</strong> for a studio or one-bedroom,
                and <strong>$40,000 to $60,000+</strong> for larger apartments or households with more electronics,
                furniture, and clothing. Count your belongings room by room rather than guessing.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>What personal property coverage pays for</h3>
              <p>
                Personal property coverage (Coverage C on an HO-4 policy) helps pay to repair or replace your belongings
                after a covered loss — things like furniture, clothing, laptops, bikes, kitchenware, and decor. It does not
                cover the building itself; that is your landlord&apos;s responsibility.
              </p>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Fire, smoke, and explosion damage to your items.</li>
                <li style={{ marginBottom: '0.5rem' }}>Theft of belongings inside your unit.</li>
                <li style={{ marginBottom: '0.5rem' }}>Certain water losses, vandalism, and other named perils in your policy.</li>
                <li>Off-premises theft in many cases, subject to limits — see our guide on{' '}
                  <Link to="/knowledge-base/does-ho4-cover-theft" style={articleLinkStyle}>HO-4 theft coverage</Link>.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Room-by-room estimate guide</h3>
              <p>Use rough replacement values — what it would cost to buy similar items new today, not what you originally paid.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Area</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Typical items to count</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Rough range</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Bedroom</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Bed, mattress, dresser, clothing, shoes</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$3,000 – $8,000</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Living room</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Sofa, TV, console, speakers, decor</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$2,000 – $6,000</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Kitchen</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Appliances you own, cookware, small appliances</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$1,000 – $4,000</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Electronics</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Laptop, phone, tablet, gaming gear, monitors</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>$2,000 – $8,000+</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Everything else</td>
                      <td style={{ padding: '0.6rem' }}>Bikes, tools, sports gear, books, storage items</td>
                      <td style={{ padding: '0.6rem' }}>Varies widely</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common coverage limits and who they fit</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}><strong>$10,000 – $15,000:</strong> Minimalist setups, dorm-style living, or very budget-focused coverage.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>$20,000 – $30,000:</strong> Typical first apartment with basic furniture and a laptop.</li>
                <li style={{ marginBottom: '0.5rem' }}><strong>$40,000 – $50,000:</strong> Two-bedroom units, couples, or renters with higher-end electronics.</li>
                <li><strong>$60,000+:</strong> Large households, home offices, or collectors with valuable items needing scheduled endorsements.</li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How to choose your limit in 5 steps</h3>
              <ol style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Walk through each room and list major items with approximate replacement cost.</li>
                <li style={{ marginBottom: '0.5rem' }}>Add 10–15% buffer for smaller items you did not individually list (dishes, linens, accessories).</li>
                <li style={{ marginBottom: '0.5rem' }}>Check sub-limits for jewelry, bikes, and electronics — standard policies often cap high-value categories.</li>
                <li style={{ marginBottom: '0.5rem' }}>Decide between{' '}
                  <Link to="/knowledge-base/actual-cash-value-vs-replacement-cost-renters-insurance" style={articleLinkStyle}>actual cash value vs. replacement cost</Link>{' '}
                  — replacement cost pays more at claim time but may cost slightly more in premium.
                </li>
                <li>Pair your property limit with an adequate{' '}
                  <Link to="/knowledge-base/how-much-liability-do-i-need-for-renters-insurance" style={articleLinkStyle}>liability limit</Link>{' '}
                  and a{' '}
                  <Link to="/knowledge-base/what-deductible-should-i-choose-for-ho4" style={articleLinkStyle}>deductible</Link>{' '}
                  you can afford out of pocket.
                </li>
              </ol>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common mistakes to avoid</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>Defaulting to the lowest limit without inventorying belongings.</li>
                <li style={{ marginBottom: '0.5rem' }}>Forgetting clothing value — wardrobes often total thousands of dollars.</li>
                <li style={{ marginBottom: '0.5rem' }}>Assuming your landlord&apos;s insurance covers your stuff (it does not).</li>
                <li>Ignoring roommate setups — see{' '}
                  <Link to="/knowledge-base/does-ho4-cover-roommates" style={articleLinkStyle}>does HO-4 cover roommates</Link>{' '}
                  for shared-policy considerations.
                </li>
              </ul>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Compare HO-4 options and choose a personal property limit that matches what you actually own.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Not sure what limit fits your setup?</h3>
              <p style={{ marginBottom: '1rem' }}>An agent can help you estimate coverage based on your apartment size and belongings.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4PersonalPropertyCoverageArticlePage;
